import { Box } from '@mui/material';

import { reportSettings, type PeReport } from '../types';
import { formatRate, formatShareOfActiveEls, formatYoYDelta } from '../utils/formatGrowth';
import { schoolYearEndYear } from '../utils/schoolYear';
import { studentsHref } from '../listKeys';
import { CountLink } from './CountLink';
import { HeadlineCard, type HeadlineCardProps } from './HeadlineCard';

const MONITORED_UNAVAILABLE = 'Monitored counts are not in this report yet';
const ASSIGNMENTS_UNAVAILABLE = 'Assignment counts are not in this report yet';
const FLASHLIGHT_UNAVAILABLE = 'Flashlight data not yet available for this district';
const NO_ACCESS_RESULTS = 'No ACCESS results in this report';
const DUALLY_IDENTIFIED_ABSENT = 'Your SIS export lacks the dually-identified field';

/**
 * This card counts students whose scores met the exit criteria whether or not the
 * district has recorded the exit. Its neighbours run off program status and exit
 * date, so a district that never records those exits keeps seeing these students
 * as active and never as monitored. This is the only place the page says so.
 */
const MET_EXIT_CRITERIA_TOOLTIP =
  'You may still need to mark these students as exited in your SIS so they move into ' +
  'monitoring status. Until then, they won’t show up in any numbers tied to exit date or ' +
  'status.';

function joinSubline(parts: (string | null)[]): string | null {
  const present = parts.filter((part): part is string => part !== null && part !== '');
  return present.length > 0 ? present.join(' · ') : null;
}

const linkFor = (count: number, key: string): string | undefined =>
  count > 0 ? studentsHref(key) : undefined;

export function HeadlineNumbers({ report }: { report: PeReport }) {
  const { district, enrollment, flashlight, reclassification, ltel, subgroups, data_quality } =
    report;
  const settings = reportSettings(report);

  const inProgram = district.in_program_count ?? district.el_student_count;
  const exited =
    district.in_program_count != null ? (district.exited_this_year_count ?? null) : null;
  const share = (count: number) => formatShareOfActiveEls(count, inProgram);

  const elDelta =
    district.el_student_count_last_year != null
      ? district.el_student_count - district.el_student_count_last_year
      : null;
  const servedIsActive = district.in_program_count == null || district.in_program_count === district.el_student_count;
  const yoy = elDelta !== null && servedIsActive ? formatYoYDelta(elDelta) : null;
  const activeSubline =
    exited != null ? (
      <>
        <CountLink count={exited} listKey="exited">{`${exited} exited this year`}</CountLink>
        {yoy ? ` · ${yoy}` : ''}
      </>
    ) : null;

  const monitoredCount = district.monitored_student_count;
  const monitoredDelta =
    monitoredCount != null && district.monitored_student_count_last_year != null
      ? monitoredCount - district.monitored_student_count_last_year
      : null;

  const eligibleCount = reclassification.this_year.eligible_count;
  const accessYear = schoolYearEndYear(district.school_year);

  const newcomerDelta = subgroups.newcomer?.year_over_year_change ?? null;

  const duallyIdentifiedSize = subgroups.dually_identified?.cohort_size ?? null;

  const cards: HeadlineCardProps[] = [
    {
      title: 'Active EL students',
      headline: `${inProgram}`,
      href: linkFor(inProgram, 'active'),
      unit: 'students',
      yoy: activeSubline === null && yoy ? yoy : undefined,
      subline: activeSubline,
    },
    {
      title: 'Monitored EL students',
      headline: monitoredCount != null ? `${monitoredCount}` : '',
      href: monitoredCount != null ? linkFor(monitoredCount, 'monitored') : undefined,
      unit: 'students',
      yoy: monitoredDelta !== null ? formatYoYDelta(monitoredDelta) : undefined,
      unavailable: monitoredCount == null ? MONITORED_UNAVAILABLE : undefined,
    },
    {
      title: 'Met exit criteria',
      headline: eligibleCount !== null ? `${eligibleCount}` : '',
      href: eligibleCount !== null ? linkFor(eligibleCount, 'met') : undefined,
      unit: 'students',
      subline:
        eligibleCount !== null
          ? joinSubline([share(eligibleCount), accessYear !== null ? `${accessYear} ACCESS` : null])
          : null,
      tooltip: MET_EXIT_CRITERIA_TOOLTIP,
      unavailable: eligibleCount === null ? NO_ACCESS_RESULTS : undefined,
    },
    {
      title: 'Newcomer ELs',
      headline: `${enrollment.newcomer_count}`,
      href: linkFor(enrollment.newcomer_count, 'newcomer'),
      unit: 'students',
      yoy: newcomerDelta !== null ? formatYoYDelta(newcomerDelta) : undefined,
      subline: `first ${settings.newcomerMaxYears} years classified EL`,
    },
    {
      title: 'Long-term ELs',
      headline: `${ltel.count}`,
      href: linkFor(ltel.count, 'ltel'),
      unit: 'students',
      subline: share(ltel.count),
    },
    {
      title: 'Dually Identified',
      headline: duallyIdentifiedSize !== null ? `${duallyIdentifiedSize}` : '',
      href: duallyIdentifiedSize !== null ? linkFor(duallyIdentifiedSize, 'dual') : undefined,
      unit: 'students',
      subline: duallyIdentifiedSize !== null ? share(duallyIdentifiedSize) : null,
      unavailable: duallyIdentifiedSize === null ? DUALLY_IDENTIFIED_ABSENT : undefined,
    },
    {
      title: 'Flashlight benchmarks',
      headline: formatRate(data_quality.flashlight_coverage_pct),
      unit: 'participation',
      unavailable: flashlight.is_integrated ? undefined : FLASHLIGHT_UNAVAILABLE,
    },
    {
      title: 'Assignments completed',
      headline: '',
      unit: '/ student',
      unavailable: flashlight.is_integrated ? ASSIGNMENTS_UNAVAILABLE : FLASHLIGHT_UNAVAILABLE,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 2,
      }}
    >
      {cards.map((card) => (
        <HeadlineCard key={card.title} {...card} />
      ))}
    </Box>
  );
}
